import { Icon, IconSource } from '@shopify/polaris'
import { MicrophoneIcon, TextIcon } from '@shopify/polaris-icons'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setAiModalActive } from '../../core/redux/store/aiSlice/aiSlice'
// import { functions } from '../../services/firebase/firebaseConfig'
// import { httpsCallable } from 'firebase/functions'
import ActionDisplay, { ActionResponse, Action } from './ActionDisplay'
import { buildSessionId } from './buildSessionId'
import ConfirmationCard from './ConfirmationCard'
import styles from './AIAssistant.module.css'
import SuggestionChips from './SuggestionChips'
import TextInput from './TextInput'
import VoiceInput from './VoiceInput'


// Module: AI Assistant chat integration with n8n (HTTPS-safe)
// - Resolves the n8n base URL in a way that avoids mixed-content on HTTPS pages
// - Allows using a same-origin proxy path (e.g., via Cloudflare Workers/Pages Functions)
// - Adds basic null checks for configuration

// Add the API endpoint path
// const AI_AGENT_ENDPOINT = `${AI_AGENT_URL}/api/process-command`;
const RAW_N8N_URL = process.env.REACT_APP_N8N_URL || ""
const N8N_PROXY_PATH = process.env.REACT_APP_N8N_PROXY_PATH || "" // optional: relative path like '/api/n8n'
const isBrowser = typeof window !== 'undefined'
const isHttpsPage = isBrowser ? window.location.protocol === 'https:' : false

// Resolve a safe base to call. Prefer an HTTPS URL; otherwise, allow a same-origin proxy path.
const RESOLVED_N8N_BASE = (() => {
    if (RAW_N8N_URL) {
        if (isHttpsPage && RAW_N8N_URL.startsWith('http://')) {
            if (N8N_PROXY_PATH) {
                return N8N_PROXY_PATH.replace(/\/$/, '')
            }
            console.warn('AIAssistant: Insecure REACT_APP_N8N_URL detected on an HTTPS page. Configure an HTTPS n8n endpoint or set REACT_APP_N8N_PROXY_PATH to a same-origin proxy.')
            return RAW_N8N_URL.replace(/\/$/, '')
        }
        return RAW_N8N_URL.replace(/\/$/, '')
    }
    if (N8N_PROXY_PATH) return N8N_PROXY_PATH.replace(/\/$/, '')
    return ''
})()

const user = process.env.REACT_APP_N8N_USER || "test"
const password = process.env.REACT_APP_N8N_PASSWORD || "test"
const sessionId = buildSessionId(user)
const webhookId = process.env.REACT_APP_CHAT_WEBHOOK_ID || "2b11debb-5c65-4bce-865d-6d649eeb304c"
// Allow switching to Firebase proxy via env flag or auto if URL provided
// const USE_FIREBASE_PROXY = ((process.env.REACT_APP_USE_FIREBASE_N8N_PROXY || 'false').toLowerCase() === 'true')
// const FIREBASE_PROXY_URL = process.env.REACT_APP_FIREBASE_N8N_PROXY_URL || '' // e.g., https://REGION-PROJECT.cloudfunctions.net/AI-n8nChatProxy
// const IS_USING_FIREBASE_PROXY = (!!FIREBASE_PROXY_URL) || USE_FIREBASE_PROXY

const AI_AGENT_ENDPOINT = process.env.REACT_APP_FIREBASE_N8N_PROXY_URL || (RESOLVED_N8N_BASE ? `${RESOLVED_N8N_BASE}/webhook/${webhookId}/chat` : '')

const SUGGESTIONS = [
    'Create new order for customer 12345678 with invoice ABC123',
    'Schedule installation for order 87654321 with vendor InstallCo',
    'Track shipment ABC123456 from vendor ShipCo',
    'Reschedule order 98765432 for June 15th with vendor ScheduleCo',
    'Create BOL for invoice BOL123 with vendor XYZ Shipping',
    'Create a new order for customer John Doe with invoice INV456',
    'Retrigger the billing for order 12345678 from vendor BillingCo',
    'Confirm the billing & settlements for last week for distribution zone North'
]

// Add new fading suggestion examples
const FADING_SUGGESTIONS = [
    'Retrigger order with invoice abc',
    'Retrigger orders between july 22-25',
    'Push tomorrow orders to drivers',
    'Push John Doe\'s orders to him for tomorrow',
    'Geocode addresses for zone north',
    'Create BOL for shipment 12345',
    'Schedule installation for next week',
    'Move order 98765 back to unassigned'
]

function AIAssistant() {
    const dispatch = useDispatch()
    const isOpen = useSelector((state: any) => state?.ai?.isModalActive || false)
    const userEmail = useSelector((state: any) => state?.user?.email || "")
    const [inputMode, setInputMode] = useState('text')
    const [actionData, setActionData] = useState<ActionResponse | null>(null)
    const [currentSuggestion, setCurrentSuggestion] = useState(0)
    const [textInputSuggestion, setTextInputSuggestion] = useState('')
    
    // Debug log for textInputSuggestion changes
    useEffect(() => {
        console.log('AIAssistant: textInputSuggestion changed to:', textInputSuggestion)
    }, [textInputSuggestion])
    const [chatMessages, setChatMessages] = useState<any[]>([]) 
    const [loadingChatMessage, setLoadingChatMessage] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [pendingActions, setPendingActions] = useState<Record<string, any>>({})
    const chatHistoryRef = useRef<HTMLDivElement | null>(null)
    
    // Add state for fading suggestions
    const [currentFadingSuggestion, setCurrentFadingSuggestion] = useState(0)
    const [fadeOpacity, setFadeOpacity] = useState(1)

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.shiftKey && event.code === 'Backslash') {
                event.preventDefault()
                dispatch(setAiModalActive(!isOpen))
            }
        }

        window.addEventListener('keydown', handleKeyPress)
        return () => window.removeEventListener('keydown', handleKeyPress)
    }, [dispatch, isOpen])

    useEffect(() => {
        if (isOpen) {
            const interval = setInterval(() => {
                setCurrentSuggestion((prev) => (prev + 1) % SUGGESTIONS.length)
            }, 4000)
            return () => clearInterval(interval)
        }
    }, [isOpen])

    // Add new useEffect for fading suggestions
    useEffect(() => {
        if (isOpen) {
            const fadeInterval = setInterval(() => {
                // Fade out
                setFadeOpacity(0)
                
                // After fade out, change suggestion and fade in
                setTimeout(() => {
                    setCurrentFadingSuggestion((prev) => (prev + 1) % FADING_SUGGESTIONS.length)
                    setFadeOpacity(1)
                }, 500)
            }, 3000)
            
            return () => clearInterval(fadeInterval)
        }
    }, [isOpen])

    const closeModal = () => {
        setActionData(null)
        dispatch(setAiModalActive(false))
    }

    const handleCommand = async (command) => {
        try {
            setActionData(null)
            setTextInputSuggestion('')
            setIsLoading(true)
            
            if (!AI_AGENT_ENDPOINT) {
                throw new Error('AI agent endpoint is not configured. Set REACT_APP_N8N_URL (HTTPS) or REACT_APP_N8N_PROXY_PATH.')
            }
            console.log(`Sending command to ${AI_AGENT_ENDPOINT}: ${command}`)
            
            // Prepare the request payload
            const payload = { 
                command,
                query: command, // Add query field for compatibility
                // You can add additional context here if needed
                context: {
                    userId: 'current-user-id', // Replace with actual user ID if available
                    timestamp: new Date().toISOString()
                }
            };
            
            console.log('Request payload:', payload);

            // Call the external AI agent via Cloud Run
            const response = await fetch(AI_AGENT_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),  
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error(`API request failed with status ${response.status}: ${errorText}`);
                throw new Error(`API request failed with status ${response.status}: ${errorText}`);
            }
            
            const result = await response.json();
            console.log('Received response:', result);
            
            // Process the response
            if (result.success && result.data) {
                setActionData({ data: result.data });
            } else if (!result.success && result.error) {
                // Handle error response
                console.error('Error from API:', result.error);
                setActionData({ 
                    error: result.error,
                    details: result.details, // Capture detailed error information if available
                    data: {
                        action: 'error',
                        message: 'Failed to process command',
                        parameters: { error: result.error }
                    }
                });
            } else {
                // Fallback for unexpected response format
                console.warn('Unexpected response format:', result);
                setActionData({ 
                    data: {
                        action: 'process_command',
                        message: 'Command processed with unexpected response format',
                        parameters: { command, rawResponse: JSON.stringify(result) }
                    }
                });
            }
        } catch (error: any) {
            console.error('Error processing command:', error);
            setActionData({ 
                error: error.message || 'Unable to process your request at this time. The AI agent service may be unavailable or experiencing issues.'
            });
        } finally {
            setIsLoading(false);
        }
    }

    const handleChat = async (message, attachments) => {
        try {
            // Add the user's message to the chat history
            setLoadingChatMessage(true)
            !message.startsWith("confirm:") && setChatMessages(prev => ([...prev, {from: "user", message, type: "text"}]));

            // Scroll to the bottom of the chat to show the message sent
            requestAnimationFrame(() => {
                if(chatHistoryRef?.current) {
                    chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight
                }
            })

            // Prepare the request payload
            const data = new FormData();
            data.append('sessionId', sessionId)
            data.append('chatInput', message)
            data.append('userEmail', userEmail)
            data.append('timestamp', new Date().toISOString())
            data.append('projectId', process.env.REACT_APP_CLIENT || "")
            if (attachments && attachments[0] && attachments[0].file) {
                data.append('file', attachments[0].file)
            }

            // Call the n8n workflow 
            if (!AI_AGENT_ENDPOINT) {
                throw new Error('AI agent endpoint is not configured. Set REACT_APP_N8N_URL (HTTPS) or REACT_APP_N8N_PROXY_PATH.')
            }
            const response = await fetch(AI_AGENT_ENDPOINT, {
                method: 'POST',
                body: data,
                headers: {
                    "Authorization": `Basic ${btoa(`${user}:${password}`)}`,
                }
            });
            
            // Add the response to the chat history
            const result = await response.json() 

            // Check for MCP/tool execution problems
            if (result.message && result.message.toLowerCase().includes('mcp / tool execution problem detected')) {
                setChatMessages(prev => ([...prev, {
                    from: "ai", 
                    message: "Oops! We were not able to process your request. Please try again. If the issue persists, please contact admin@shyftbase.com.",
                    type: "text"
                }]))
            }
            // Check if the response contains confirmation data
            else if (result.confirm) {
                setChatMessages(prev => ([...prev, {
                    from: "ai", 
                    message: result.message || "Please confirm the details below:",
                    type: "confirm",
                    data: result, // Pass the entire result object since it contains all necessary info
                    operation: result.operation
                }]))
            } else {
                setChatMessages(prev => ([...prev, {from: "ai", message: result.message, type: "text"}]))
            }
            
            // Clear the text input suggestion after processing the response
            setTextInputSuggestion('')
            setLoadingChatMessage(false)

            // Scroll to the bottom of the chat to show the response
            requestAnimationFrame(() => {
                if(chatHistoryRef?.current) {
                    chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight
                }
            })
        }
        catch(e){
            setLoadingChatMessage(false)
            console.log({e})
        }
            
    }

    const handleChipClick = (suggestion: string) => {
        setTextInputSuggestion(suggestion)
        setInputMode('text')
    }

    const handleConfirm = async (operation: string, data: any) => {
        try {
            // Add loading state
            setPendingActions(prev => ({ ...prev, [operation]: { operation, data } }))
            
            // Add confirmation flag to data
            const dataWithConfirmation = {
                ...data,
                confirmationFlag: true
            }
            
            // Create confirmation command for the agent
            const confirmCommand = `confirm: ${operation} with data: ${JSON.stringify(dataWithConfirmation)}`
            
            // Add user's confirmation to chat
            setChatMessages(prev => ([...prev, {
                from: "ai",
                message: "Proceeding with " + operation + "...",
                type: "text"
            }]))
            
            // Send confirmation command to agent
            await handleChat(confirmCommand, [])
        } catch (error) {
            console.error('Confirmation failed:', error)
            setChatMessages(prev => ([...prev, {
                from: "ai",
                message: `❌ Failed to confirm ${operation}. Please try again.`,
                type: "text"
            }]))
        } finally {
            // Remove from pending actions
            setPendingActions(prev => {
                const newPending = { ...prev }
                delete newPending[operation]
                return newPending
            })
            // Clear the text input suggestion after confirmation
            setTextInputSuggestion('')
        }
    }

    const title = 'Shyftbase AI'

    if (!isOpen) return null

    return (
        <div className="modal is-active ai-assistant" style={{ background: "#fff", padding: 0, margin: 0, width: 500, left: "unset", boxShadow: "0 0 30px #aaa" }}>
            <svg style={{ position: 'absolute', width: 0, height: 0 }}>
                <defs>
                    <filter id="wave-distortion">
                        <feTurbulence type="turbulence" baseFrequency="0.02 0.15" numOctaves="2" result="turbulence" />
                        <feDisplacementMap in="SourceGraphic" in2="turbulence" scale="10" />
                    </filter>
                </defs>
            </svg>
            <div
                className="modal-content"
                style={{ height: '100vh', minHeight: '100vh', overflowY: 'hidden', padding: '20px', paddingTop: 30, minWidth: 500,  width: 500, display: 'flex', flexDirection: 'column' }}
            >
                {/* Fixed Header */}
                <div className="level" style={{ flexShrink: 0 }}>
                    <div className="level-left">
                        <div className="level-item">
                            <div className="title-container">
                                <h1 className="is-5 title font-bold">
                                    {title.split('').map((char, index) =>
                                        char === ' ' ? (
                                            <span key={index}>&nbsp;</span>
                                        ) : (
                                            <span
                                                key={index}
                                                className="animated-char"
                                                style={{
                                                    animationDelay: `${index * 0.05}s`,
                                                }}
                                            >
                                                {char}
                                            </span>
                                        )
                                    )}
                                </h1>
                            </div>
                        </div>
                    </div>
                    {/* <div className="level-item">
                        <div className="has-text-centered">
                            <div className={styles.suggestionText}>{SUGGESTIONS[currentSuggestion]}</div>
                        </div>
                    </div> */}
                    <div className="level-right">
                        <button
                            className={`button ${inputMode === 'voice' ? 'is-primary' : 'is-dark'} is-small`}
                            onClick={() => setInputMode('voice')}
                        >
                            <span className="icon">
                                <Icon source={MicrophoneIcon as IconSource} />
                            </span>
                        </button>
                        <button
                            className={`button ${inputMode === 'text' ? 'is-primary' : 'is-dark'} is-small`}
                            onClick={() => setInputMode('text')}
                        >
                            <span className="icon">
                                <Icon source={TextIcon as IconSource} />
                            </span>
                        </button>
                    </div>
                </div>
                
                {/* Scrollable Chat History */}
                <div 
                    className="chat-history" 
                    ref={chatHistoryRef} 
                    style={{ 
                        marginBottom: 10, 
                        flex: 1,
                        overflowY: "auto",
                        fontSize: "0.85rem",
                        scrollBehavior: "smooth"
                    }}
                >
                    {
                        chatMessages.map((e, index) => {
                            if (e.type === 'confirm') {
                                console.log({e})
                                return (
                                    <div key={index} style={{ margin: "5px" }}>
                                        <ConfirmationCard
                                            message={e.message}
                                            data={e.data}
                                            operation={e.operation}
                                            title={`${e.operation} Request`}
                                            onConfirm={handleConfirm}
                                        />
                                    </div>
                                )
                            } else {
                                return (
                                    <div 
                                        style={{ 
                                            textAlign: "left",
                                            direction: e.from === "ai" ? "ltr" : "rtl",
                                            display: "flex",
                                            margin: "5px 10px",
                                        }} 
                                        key={index} 
                                        className={`chat-message ${e.from === "ai" ? "ai-message" : "user-message"}`}>
                                        <span style={{
                                            borderRadius: 25,
                                            padding: "5px 0px",
                                            display: "inline-block",
                                            background: e.from === "ai" ? "transparent" : "#fff",
                                        }}>{e.from === "ai" ? <pre style={{padding: 0, background: "transparent", whiteSpace: "pre-wrap"}}>{e.message}</pre> : e.message}</span>
                                    </div>
                                )
                            }
                        })
                    }
                    {loadingChatMessage ? <div className="ui loader" style={{ borderBottomColor: "#666"}} /> : null}
                </div>

                {/* Fixed Bottom Section */}
                <div style={{ flexShrink: 0 }}>
                    {/* Fading Suggestion Line */}
                    <div 
                        className="has-text-centered mb-3"
                        style={{
                            color: '#666',
                            fontSize: '0.85rem',
                            fontStyle: 'italic',
                            minHeight: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <span
                            style={{
                                opacity: fadeOpacity,
                                transition: 'opacity 0.5s ease-in-out',
                                textAlign: 'center'
                            }}
                        >
                            &quot;{FADING_SUGGESTIONS[currentFadingSuggestion]}&quot;
                        </span>
                    </div>

                    {inputMode === 'voice' ? (
                        <VoiceInput onCommand={handleCommand} autoFocus={true} />
                    ) : (
                        <TextInput 
                            onCommand={handleChat} 
                            autoFocus={true} 
                            initialSuggestion={textInputSuggestion}
                        />
                    )}

                    <SuggestionChips onChipClick={handleChipClick} />
                    
                    {isLoading && (
                        <div className="has-text-centered my-4">
                            <div className="button is-loading is-primary">Processing</div>
                            <p className="mt-2 has-text-grey">Analyzing your request and creating order...</p>
                        </div>
                    )}

                    <ActionDisplay actionData={actionData} />
                </div>
                <button className="modal-close is-large" aria-label="close" onClick={closeModal} style={{right: 515, background: "#bbb"}}></button>

            </div>
        </div>
    )
}

export default React.memo(AIAssistant)
